import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'src/app/layout/shared/models/menu.model';
import { NotificationOptions } from 'src/app/notifier-options';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { User } from '../models/auth.models';
import { AuthenticationService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  private Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    showCloseButton: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer)
      toast.addEventListener('mouseleave', Swal.resumeTimer)
    }
  });

  constructor(protected translate: TranslateService,
    protected authSrv: AuthenticationService) { }

  /** Validations */
  validatePositiveDecimalNumbers(event: any) {
    const value = event.target.value;
    const min = parseInt(event.target.min);
    const max = parseInt(event.target.max);
    const valueNow = parseFloat(value?.toString() + event.key);
    // if (min && valueNow && valueNow < min) return false;
    // if (max && valueNow && valueNow > max) return false;
    return (event.charCode == 8 || event.charCode == 0 || event.charCode == 13) ? false : event.charCode >= 48 && event.charCode <= 57 || (event.charCode === 44 || event.charCode === 46)
  }

  validatePositiveIntegerNumbers(event: any) {
    const value = event.target.value;
    const min = parseInt(event.target.min);
    const max = parseInt(event.target.max);
    const valueNow = parseFloat(value?.toString() + event.key);
    // if (min && valueNow && valueNow < min) return false;
    // if (max && valueNow && valueNow > max) return false;
    return (event.charCode == 8 || event.charCode == 0 || event.charCode == 13) ? null : event.charCode >= 48 && event.charCode <= 57
  }

  validateDate(event: any) {
    return event.charCode >= 48 && event.charCode <= 57 || event.charCode === 47;
  }

  //Others
  getMaxObjFromList(list: any[]) {
    var ids = list.map(object => {
      return object.id;
    });
    var maxValue = Math.max.apply(null, ids);
    var index = list.findIndex((f: any) => f.id === maxValue);
    return index;
  }

  getSringInitials(name: string) {
    const fullName: string[] = name.toUpperCase().split(' ');
    const firstWord = fullName.shift();
    const lastWord = fullName.pop();
    return ''.concat(firstWord ? firstWord.charAt(0) : '', lastWord ? lastWord.charAt(0) : '');
  }

  getCountFiles(uploadedFiles: any) {
    return uploadedFiles && uploadedFiles.length > 0 ? uploadedFiles.filter((f: any) => !f.deleted || f.deleted === false).length : 0;
  }

  getUserName(user: any) {
    return new User(user).getName(this.translate.currentLang);
  }

  getUserInitials(user: any) {
    return new User(user).getInitials(this.translate.currentLang);
  }

  getContrastYIQ(hexcolor: any) {
    hexcolor = hexcolor.replace("#", "");
    var r = parseInt(hexcolor.substr(0, 2), 16);
    var g = parseInt(hexcolor.substr(2, 2), 16);
    var b = parseInt(hexcolor.substr(4, 2), 16);
    var yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? 'black' : 'white';
  }

  /** Cards */
  getRowNumbers(total: number, windowSize: number) {
    let perLine = this.getCardPerLine(windowSize);
    return (perLine == 0) ? 0 : Math.ceil(total / perLine)
  }

  getCardPerLine(windowSize: number) {
    let perLine = 0;

    if (windowSize < 576) perLine = 1;
    else if (windowSize >= 576 && windowSize < 768) perLine = 2;
    else if (windowSize >= 768 && windowSize < 1800) perLine = 3;
    else perLine = 4;

    return perLine;
  }

  /** SWAL */
  confirmWtext(title: string, text: string, confirmBtn: string = 'MISC.CONFIRM', cancelBtn: string = 'MISC.CANCEL', icon: SweetAlertIcon = NotificationOptions.Question) {
    return Swal.fire({
      position: 'center',
      icon: icon,
      title: this.translate.instant(title),
      text: this.translate.instant(text),
      width: 400,
      heightAuto: true,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: this.translate.instant(confirmBtn),
      cancelButtonText: this.translate.instant(cancelBtn),
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false
    });
  }

  async confirmTextWithNote(title: string, confirmBtn: string = 'MISC.CONFIRM', cancelBtn: string = 'MISC.CANCEL', icon: SweetAlertIcon = NotificationOptions.Question) {
    return new Promise((resolve, reject) => {
      Swal.fire({
        title: this.translate.instant(title),
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: this.translate.instant(confirmBtn),
        cancelButtonText: this.translate.instant(cancelBtn),
        input: "textarea",
        inputPlaceholder: "",
        inputAttributes: {
          "aria-label": "Type your message here"
        },
      }).then(function (result) {
        if (!result.isConfirmed) {
          reject(null)
          return;
        }

        resolve({
          ...result,
          value: result.value ?? null
        })
      })
    })
  }

  confirm(title: string, confirmBtn: string = 'MISC.CONFIRM', cancelBtn: string = 'MISC.CANCEL', icon: SweetAlertIcon = NotificationOptions.Question) {
    return Swal.fire({
      position: 'center',
      icon: icon,
      title: this.translate.instant(title),
      width: 400,
      heightAuto: true,
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: this.translate.instant(confirmBtn),
      cancelButtonText: this.translate.instant(cancelBtn),
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false
    });
  }

  notify(title: string, icon: SweetAlertIcon, translate: boolean = true) {
    return Swal.fire({
      position: 'center',
      icon: icon,
      title: translate ? this.translate.instant(title) : title,
      width: 400,
      heightAuto: true,
      showConfirmButton: false,
      timer: 5000
    });
  }

  toast(title: string, icon: SweetAlertIcon = 'success', translate: boolean = true) {
    return this.Toast.fire({
      icon: icon,
      title: translate ? this.translate.instant(title) : title
    });
  }
}
